import PublicLayout from "@/components/PublicLayout";

export default function TermsOfService() {
  return (
    <PublicLayout title="Terms of Service" lastUpdated="Last Updated: June 2026">
      <h2>1. Introduction</h2>
      <p>Welcome to NextRota. These Terms of Service (“Terms”) govern your access to and use of the NextRota website, platform, applications, and related services (collectively, the “Services”). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree to these Terms, you must not use the Services. Throughout these Terms, “NextRota”, “we”, “us”, and “our” refer to NextRota.</p>

      <h2>2. Eligibility</h2>
      <p>You must be at least 18 years old and capable of entering into legally binding agreements to use the Services. If you use the Services on behalf of a business, organisation, or employer, you represent that you have authority to bind that entity to these Terms.</p>

      <h2>3. Account Registration</h2>
      <p>To access certain features, you may be required to create an account. You agree to:</p>
      <ul>
        <li>Provide accurate information</li>
        <li>Keep account details current</li>
        <li>Maintain the security of your credentials</li>
        <li>Notify us promptly of any unauthorised use</li>
      </ul>
      <p>You are responsible for all activity occurring under your account.</p>

      <h2>4. Description of Services</h2>
      <p>NextRota provides workforce management and scheduling tools, including but not limited to:</p>
      <ul>
        <li>Employee scheduling</li>
        <li>Availability management</li>
        <li>Leave management</li>
        <li>Workforce planning</li>
        <li>Shift allocation</li>
        <li>Reporting and analytics</li>
        <li>Team communication tools</li>
        <li>AI-assisted recommendations and scheduling features</li>
      </ul>
      <p>Features may change, be modified, or be discontinued at any time.</p>

      <h2>5. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Violate any applicable law</li>
        <li>Use the Services for unlawful purposes</li>
        <li>Attempt to gain unauthorised access to systems or data</li>
        <li>Interfere with platform security</li>
        <li>Upload malicious software</li>
        <li>Reverse engineer the platform except where permitted by law</li>
        <li>Use the Services to infringe intellectual property rights</li>
        <li>Misrepresent your identity or affiliation</li>
      </ul>

      <h2>6. User Content</h2>
      <p>You retain ownership of content and information you upload to the Services. By uploading content, you grant NextRota a non-exclusive, worldwide, royalty-free licence to host, process, store, and display such content solely for the purpose of operating and providing the Services. You are responsible for ensuring that you have the necessary rights to upload any content.</p>

      <h2>7. Subscription Plans and Payments</h2>
      <p>Certain Services may require payment. Subscription fees, billing periods, and available plans will be presented before purchase. By purchasing a subscription, you authorise the applicable payment processor to charge the selected payment method. Failure to pay subscription fees may result in suspension or termination of access.</p>

      <h2>8. Renewals and Cancellations</h2>
      <p>Subscriptions may automatically renew unless cancelled before the renewal date. Users may cancel subscriptions through their account settings or by contacting us. Fees already paid are generally non-refundable unless required by law or otherwise stated.</p>

      <h2>9. Intellectual Property</h2>
      <p>The Services, including software, designs, trademarks, branding, logos, graphics, content, and technology, are owned by or licensed to NextRota. Except as expressly permitted by these Terms, no rights are granted to users. The NextRota name, logo, and branding may not be used without prior written permission.</p>

      <h2>10. Artificial Intelligence Features</h2>
      <p>Certain features may use artificial intelligence or automated technologies. AI-generated outputs are provided for informational and operational assistance purposes only. Users remain responsible for reviewing and validating scheduling decisions, recommendations, reports, and other outputs generated by AI-assisted functionality. NextRota does not guarantee the accuracy, completeness, or suitability of AI-generated results.</p>

      <h2>11. Availability of Services</h2>
      <p>We aim to provide reliable Services but do not guarantee uninterrupted availability. Services may be unavailable due to:</p>
      <ul>
        <li>Maintenance</li>
        <li>Updates</li>
        <li>Technical issues</li>
        <li>Third-party service interruptions</li>
        <li>Circumstances beyond our reasonable control</li>
      </ul>

      <h2>12. Third-Party Services</h2>
      <p>The Services may integrate with third-party providers, including payment processors, communication platforms, authentication services, and business software. We are not responsible for third-party services or their availability, security, or performance. Use of third-party services may be subject to separate terms and policies.</p>

      <h2>13. Suspension and Termination</h2>
      <p>We may suspend or terminate access if:</p>
      <ul>
        <li>These Terms are violated</li>
        <li>Payment obligations are not met</li>
        <li>Use creates security risks</li>
        <li>Use exposes us to legal liability</li>
      </ul>
      <p>Users may stop using the Services at any time. Termination does not affect accrued rights or obligations.</p>

      <h2>14. Disclaimer of Warranties</h2>
      <p>The Services are provided on an “as is” and “as available” basis. To the fullest extent permitted by law, NextRota disclaims all warranties, whether express, implied, statutory, or otherwise, including warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the Services will be error-free, secure, uninterrupted, or suitable for every use case.</p>

      <h2>15. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, NextRota shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages arising from or relating to the Services. Where liability cannot be excluded, our total liability for any claim shall not exceed the amount paid by the user to NextRota during the six months immediately preceding the event giving rise to the claim. Nothing in these Terms excludes liability that cannot be excluded under applicable law.</p>

      <h2>16. Indemnification</h2>
      <p>You agree to indemnify and hold harmless NextRota, its owners, contractors, and representatives from claims, damages, liabilities, losses, and expenses arising from:</p>
      <ul>
        <li>Your use of the Services</li>
        <li>Violation of these Terms</li>
        <li>Violation of applicable laws</li>
        <li>Infringement of third-party rights</li>
      </ul>

      <h2>17. Governing Law</h2>
      <p>These Terms shall be governed by and interpreted in accordance with the laws of England and Wales.</p>

      <h2>18. Dispute Resolution</h2>
      <p>Before commencing formal legal proceedings, both parties agree to attempt to resolve disputes through good-faith informal discussions. If a dispute cannot be resolved informally, the courts of England and Wales shall have exclusive jurisdiction unless otherwise required by applicable law.</p>

      <h2>19. Changes to These Terms</h2>
      <p>We may update these Terms from time to time. Updated versions will be published on our website and will become effective on the date indicated. Continued use of the Services following publication of updated Terms constitutes acceptance of the revised Terms.</p>

      <h2>20. Contact Information</h2>
      <p>For questions regarding these Terms, please contact:</p>
      <p>
        <strong>NextRota</strong><br/>
        Email: <a href="mailto:hello@nextrota.com">hello@nextrota.com</a><br/>
        Privacy enquiries: <a href="mailto:privacy@nextrota.com">privacy@nextrota.com</a><br/>
        Website: <a href="https://nextrota.com">https://nextrota.com</a>
      </p>
    </PublicLayout>
  );
}
